import { atom } from "jotai";

import type { DicomSeriesData } from "../components/types";
import type { StudyMetadataResponse } from "../utils/dicomMetadataLoader";

/**
 * Selection atoms for CaseViewer
 *
 * These atoms manage the selection state shared between:
 * - PredictionsDisplay (findings list)
 * - ReporterEditorPanel (report editor)
 * - ImageViewer/VolumeViewport (bbox highlighting, slice navigation)
 */

// Study metadata SSoT. useDicomSeriesLoader writes this from the React
// Query cache; downstream instance-map atoms derive off it so we don't
// duplicate the same data into three places. Pre-atomization this was
// React Query → useEffect → setAtom for two separate maps, which meant a
// single metadata fetch fanned out to three writes and the maps could
// briefly disagree with the source.
export const studyMetadataAtom = atom<StudyMetadataResponse | null>(null);

// Active spatial group (for bbox highlighting in viewer)
export const activeGroupIdAtom = atom<string | null>(null);

// Selected finding condition (for finding list highlight)
export const selectedFindingConditionAtom = atom<string | null>(null);

// Current slice index for viewer navigation
// Used by VolumeViewport to navigate to specific slices when findings are clicked
export const currentSliceAtom = atom<number>(0);

// Total slices in current series (for bounds checking)
export const totalSlicesAtom = atom<number>(0);

// Target series ID for navigation (when clicking a finding in a different series)
// null means stay on current series
export const targetSeriesIdAtom = atom<string | null>(null);

// Active series selection driven by useDicomSeriesLoader. Previously lived
// at study.selectedSeriesId in CaseViewer's useState plus a duplicate on
// the hook's return; the two could drift if a write missed one. The atom
// is the single source — the hook writes it, all consumers read it.
export const selectedSeriesIdAtom = atom<string | null>(null);

// Loaded DICOM series (imageIds + dims). useDicomSeriesLoader appends on
// successful load; CaseViewer + the loader's own callbacks read.
export const loadedSeriesAtom = atom<DicomSeriesData[]>([]);

// True while useDicomSeriesLoader's loadSeries / handleSeriesSelect work
// is in flight. Distinct from React Query's `isLoading` (which tracks the
// metadata fetch only — see studyMetadataAtom).
export const studyIsLoadingAtom = atom<boolean>(false);

// Series-load state machine + last error. Used by the loading overlay to
// distinguish "still selecting initial series" from "selected but failed".
export const seriesLoadingStateAtom = atom<"idle" | "loading" | "loaded" | "error">("idle");
export const seriesErrorAtom = atom<string | null>(null);

// SOP Instance UID (or filename) → slice index, keyed by series. Derived
// from studyMetadataAtom so it stays consistent with the source on every
// read — no bridging useEffect required. Backend pre-sorts instances by
// z_position, so array index here matches the imageId order Cornerstone
// renders.
export const instanceToSliceMapAtom = atom((get) => {
  const metadata = get(studyMetadataAtom);
  const mapping: Record<string, Record<string, number>> = {};
  if (!metadata?.series) return mapping;
  for (const [seriesId, instances] of Object.entries(metadata.series)) {
    const seriesMapping: Record<string, number> = {};
    instances.forEach((instance, index) => {
      seriesMapping[instance.instance_uid] = index;
      seriesMapping[`${instance.instance_uid}.dcm`] = index;
    });
    mapping[seriesId] = seriesMapping;
  }
  return mapping;
});

// Enhanced mapping that includes z_position for proper spatial grouping.
// Same derivation as instanceToSliceMapAtom but retains the per-instance
// z_position so callers doing spatial bbox grouping don't have to walk
// the original metadata.
export interface InstanceSliceInfo {
  index: number;
  zPosition: number;
  /** Canonical object key; viewer imageIds end with it. File names need not be SOP UIDs (ENG-269). */
  s3Key: string;
}
export const instanceSliceInfoMapAtom = atom((get) => {
  const metadata = get(studyMetadataAtom);
  const mapping: Record<string, Record<string, InstanceSliceInfo>> = {};
  if (!metadata?.series) return mapping;
  for (const [seriesId, instances] of Object.entries(metadata.series)) {
    const seriesMapping: Record<string, InstanceSliceInfo> = {};
    instances.forEach((instance, index) => {
      const zPosition = instance.z_position ?? 0;
      const info = { index, zPosition, s3Key: instance.s3_key };
      seriesMapping[instance.instance_uid] = info;
      seriesMapping[`${instance.instance_uid}.dcm`] = info;
    });
    mapping[seriesId] = seriesMapping;
  }
  return mapping;
});

// Minimum operating point count filter for findings (1-4)
// Used by PredictionsDisplay to filter findings and by ReporterEditorPanel for report generation
export const minOpCountAtom = atom<number>(1);

// Pending finding to insert into report
// Set by PredictionsDisplay when user clicks "Insert to Report" button
// Consumed by ReporterEditorPanel to insert the finding text
export const pendingReportInsertAtom = atom<{
  condition: string;
  text: string;
  section: string;
} | null>(null);

// Convenience atom combining both for components that need the full state
export const selectionStateAtom = atom(
  (get) => ({
    activeGroupId: get(activeGroupIdAtom),
    selectedFindingCondition: get(selectedFindingConditionAtom),
  }),
  (
    get,
    set,
    update: { activeGroupId?: string | null; selectedFindingCondition?: string | null },
  ) => {
    if ("activeGroupId" in update) {
      set(activeGroupIdAtom, update.activeGroupId ?? null);
    }
    if ("selectedFindingCondition" in update) {
      set(selectedFindingConditionAtom, update.selectedFindingCondition ?? null);
    }
  },
);
